/* Invoice analytics tab — the "real" analytics surface that replaces
 * the 4-card stats strip. Four modules stacked vertically:
 *
 *   1. KPI row (the four headline numbers — kept from the old strip)
 *   2. Aging report — stacked horizontal bars per bucket
 *   3. Monthly trend — 6-month invoiced vs paid columns
 *   4. Top customers — top 5 by revenue with paid/owed split
 *   5. Status breakdown — segmented bar of all invoice states
 *
 * No chart library — everything is inline SVG / divs. Same approach
 * BudgetVsActualTab took for the sparkline (and that's been merged).
 */

import React from 'react';
import {
  Wallet, CheckCircle2, AlertCircle, Clock,
  TrendingUp, Users, Layers
} from 'lucide-react';
import './InvoiceAnalytics.css';

/* ─── Helpers ────────────────────────────────────────────────── */

const STATUS_COLORS = {
  DRAFT:   { bg: '#e5e7eb', fg: '#4b5563' },
  SENT:    { bg: '#fef3c7', fg: '#92400e' },
  PARTIAL: { bg: '#dbeafe', fg: '#1e40af' },
  PAID:    { bg: '#bbf7d0', fg: '#047857' },
  OVERDUE: { bg: '#fecaca', fg: '#991b1b' },
  VOID:    { bg: '#f3f4f6', fg: '#6b7280' },
};

const AGING_COLORS = {
  '0-30':  '#bbf7d0',
  '31-60': '#fde68a',
  '61-90': '#fdba74',
  '90+':   '#fca5a5',
};

function max(arr) { return arr.reduce((m, n) => n > m ? n : m, 0); }
function sum(arr) { return arr.reduce((a, b) => a + b, 0); }

/* ─── Module 1 — KPI row (same as before, just lifted into here) ─── */
function KpiRow({ stats, formatMoney }) {
  const s = stats || {};
  return (
    <div className="ia-kpi-row">
      <Kpi icon={<Wallet size={16} />} kind="brand" label="Outstanding" value={formatMoney(s.outstanding || 0)} />
      <Kpi icon={<CheckCircle2 size={16} />} kind="ok"   label="Paid this month" value={formatMoney(s.paidThisMonth || 0)} />
      <Kpi
        icon={<AlertCircle size={16} />}
        kind={s.overdueCount > 0 ? 'warn' : 'neutral'}
        label="Overdue"
        value={formatMoney(s.overdueAmount || 0)}
        meta={s.overdueCount > 0 ? `${s.overdueCount} invoice${s.overdueCount === 1 ? '' : 's'}` : null}
      />
      <Kpi
        icon={<Clock size={16} />}
        kind="neutral"
        label="Avg. days to pay"
        value={s.paidCohortSize > 0 ? `${s.avgDaysToPay}d` : '—'}
        meta={s.paidCohortSize > 0 ? 'last 90 days' : null}
      />
    </div>
  );
}

function Kpi({ icon, kind, label, value, meta }) {
  return (
    <div className={`ia-kpi ia-kpi--${kind}`}>
      <div className={`ia-kpi-icon ia-kpi-icon--${kind}`}>{icon}</div>
      <div className="ia-kpi-body">
        <div className="ia-kpi-label">{label}</div>
        <div className="ia-kpi-value">
          {value}{meta && <span className="ia-kpi-meta"> · {meta}</span>}
        </div>
      </div>
    </div>
  );
}

/* ─── Module 2 — Aging report ───────────────────────────────── */
function AgingReport({ aging, agingCounts, formatMoney }) {
  const buckets = ['0-30', '31-60', '61-90', '90+'];
  const values = buckets.map((b) => aging?.[b] || 0);
  const totalOwed = sum(values);
  if (totalOwed === 0) {
    return (
      <Card title="A/R Aging" icon={<Layers size={14} />}>
        <div className="ia-empty">No outstanding balance. Healthy.</div>
      </Card>
    );
  }
  return (
    <Card title="A/R Aging" icon={<Layers size={14} />} subtitle="Outstanding amount by days overdue">
      <div className="ia-aging">
        <div className="ia-aging-bar">
          {buckets.map((b) => {
            const v = aging?.[b] || 0;
            const pct = totalOwed > 0 ? (v / totalOwed) * 100 : 0;
            if (pct === 0) return null;
            return (
              <div
                key={b}
                className="ia-aging-segment"
                style={{ width: `${pct}%`, background: AGING_COLORS[b] }}
                title={`${b} days — ${formatMoney(v)}`}
              />
            );
          })}
        </div>
        <ul className="ia-aging-legend">
          {buckets.map((b) => {
            const v = aging?.[b] || 0;
            const count = agingCounts?.[b] || 0;
            const pct = totalOwed > 0 ? Math.round((v / totalOwed) * 100) : 0;
            return (
              <li key={b}>
                <span className="ia-aging-dot" style={{ background: AGING_COLORS[b] }} />
                <span className="ia-aging-bucket">{b} days</span>
                <span className="ia-aging-amount">{formatMoney(v)}</span>
                <span className="ia-aging-count">{count} · {pct}%</span>
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}

/* ─── Module 3 — Monthly trend ──────────────────────────────── */
function MonthlyTrend({ monthlyTrend, formatMoney }) {
  const rows = monthlyTrend || [];
  if (rows.length === 0) return null;
  const peak = max(rows.flatMap((r) => [r.invoiced, r.paid])) || 1;
  return (
    <Card title="Monthly trend" icon={<TrendingUp size={14} />} subtitle="Invoiced vs collected, last 6 months">
      <div className="ia-trend">
        <div className="ia-trend-bars">
          {rows.map((r) => {
            const invH = (r.invoiced / peak) * 100;
            const paidH = (r.paid / peak) * 100;
            return (
              <div className="ia-trend-col" key={r.month}>
                <div className="ia-trend-stack">
                  <div className="ia-trend-bar ia-trend-bar--invoiced" style={{ height: `${invH}%` }}
                       title={`Invoiced ${formatMoney(r.invoiced)}`} />
                  <div className="ia-trend-bar ia-trend-bar--paid" style={{ height: `${paidH}%` }}
                       title={`Collected ${formatMoney(r.paid)}`} />
                </div>
                <div className="ia-trend-label">{r.month}</div>
              </div>
            );
          })}
        </div>
        <div className="ia-trend-legend">
          <span><span className="ia-dot ia-dot--invoiced" /> Invoiced</span>
          <span><span className="ia-dot ia-dot--paid" /> Collected</span>
        </div>
      </div>
    </Card>
  );
}

/* ─── Module 4 — Top customers ──────────────────────────────── */
function TopCustomers({ topCustomers, formatMoney }) {
  const rows = topCustomers || [];
  if (rows.length === 0) {
    return (
      <Card title="Top customers" icon={<Users size={14} />}>
        <div className="ia-empty">No customers yet. Send your first invoice to start tracking.</div>
      </Card>
    );
  }
  const peak = max(rows.map((r) => r.total)) || 1;
  return (
    <Card title="Top customers" icon={<Users size={14} />} subtitle="By revenue, last 12 months">
      <ul className="ia-cust">
        {rows.map((c) => {
          const totalPct = (c.total / peak) * 100;
          const paidPct  = c.total > 0 ? (c.paid / c.total) * 100 : 0;
          return (
            <li key={`${c.email}-${c.name}`}>
              <div className="ia-cust-head">
                <div className="ia-cust-name">{c.name}</div>
                <div className="ia-cust-total">{formatMoney(c.total)}</div>
              </div>
              <div className="ia-cust-bar" style={{ width: `${totalPct}%` }}>
                <div className="ia-cust-bar-paid" style={{ width: `${paidPct}%` }} />
              </div>
              <div className="ia-cust-meta">
                <span className="ia-cust-email">{c.email}</span>
                <span className="ia-cust-paid">{formatMoney(c.paid)} collected · {c.count} invoice{c.count === 1 ? '' : 's'}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* ─── Module 5 — Status breakdown ───────────────────────────── */
function StatusBreakdown({ statusBreakdown }) {
  const data = statusBreakdown || {};
  const keys = Object.keys(data).filter((k) => data[k] > 0);
  const total = sum(keys.map((k) => data[k]));
  if (total === 0) {
    return (
      <Card title="Status breakdown" icon={<Layers size={14} />}>
        <div className="ia-empty">No invoices yet.</div>
      </Card>
    );
  }
  return (
    <Card title="Status breakdown" icon={<Layers size={14} />} subtitle="Composition of all invoices issued">
      <div className="ia-status-bar">
        {keys.map((k) => {
          const pct = (data[k] / total) * 100;
          return (
            <div
              key={k}
              className="ia-status-seg"
              style={{ width: `${pct}%`, background: STATUS_COLORS[k]?.bg || '#e5e7eb' }}
              title={`${k} — ${data[k]} (${Math.round(pct)}%)`}
            />
          );
        })}
      </div>
      <ul className="ia-status-legend">
        {keys.map((k) => {
          const pct = Math.round((data[k] / total) * 100);
          return (
            <li key={k}>
              <span className="ia-status-dot" style={{ background: STATUS_COLORS[k]?.bg }} />
              <span className="ia-status-label" style={{ color: STATUS_COLORS[k]?.fg }}>{k}</span>
              <span className="ia-status-count">{data[k]} · {pct}%</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* ─── Card frame ────────────────────────────────────────────── */
function Card({ title, icon, subtitle, children }) {
  return (
    <section className="ia-card">
      <header>
        <div className="ia-card-title">
          {icon && <span className="ia-card-icon">{icon}</span>}
          {title}
        </div>
        {subtitle && <div className="ia-card-subtitle">{subtitle}</div>}
      </header>
      <div className="ia-card-body">{children}</div>
    </section>
  );
}

/* ─── Public component ──────────────────────────────────────── */
export default function InvoiceAnalytics({ stats, loading, formatMoney }) {
  if (loading && !stats) {
    return <div className="ia-loading">Loading analytics…</div>;
  }
  if (!stats) {
    return <div className="ia-loading">No data yet.</div>;
  }
  return (
    <div className="ia-page">
      <KpiRow stats={stats} formatMoney={formatMoney} />
      <div className="ia-grid">
        <AgingReport aging={stats.aging} agingCounts={stats.agingCounts} formatMoney={formatMoney} />
        <MonthlyTrend monthlyTrend={stats.monthlyTrend} formatMoney={formatMoney} />
        <TopCustomers topCustomers={stats.topCustomers} formatMoney={formatMoney} />
        <StatusBreakdown statusBreakdown={stats.statusBreakdown} />
      </div>
    </div>
  );
}
